import { SortModeType, SORT_MODE } from 'data/AllArticles/types';
import { contentfulRESTClient } from 'rest/clients';
import { getContentfulClient } from 'graphql/clients';
import { ApolloQueryResult } from 'apollo-client';
import { ArticlesBodies } from 'graphql/__generated__/operations/ArticlesBodies';
import gql from 'graphql-tag';

export const getArticlesOrder = (sortMode?: SortModeType) => {
  switch (sortMode) {
    case SORT_MODE.MOST_RECENT:
      return '-fields.editorialLastPublishedDateComputed';
    case SORT_MODE.READ_TIME:
      return 'fields.readTimeComputed,fields.readTimeHandcoded';
    default:
      return '-fields.editorialLastPublishedDateComputed';
  }
};
/**
 * Get article IDs that belong to a certain category using the category's slug.
 */
export const getArticleIdsForCategory = async ({
  slug,
  limit,
  excludeIds,
  sortMode = SORT_MODE.MOST_RECENT,
  skip = 0,
}: {
  slug: string;
  limit: number;
  excludeIds?: string;
  skip?: number;
  sortMode?: SortModeType;
}): Promise<string[]> => {
  // Get the categoryTopic's id using the categoryAssembly's slug
  const categoryTopic = await contentfulRESTClient.getEntries<{
    category: { sys: { id: string } };
  }>({
    limit: 1,
    skip: 0,
    content_type: 'categoryAssembly',
    'fields.slug': slug,
    select: 'fields.category',
  });

  const categoryTopicSysId = categoryTopic?.items[0]?.fields?.category.sys.id;

  const { items } = await contentfulRESTClient.getEntries<{
    sys: { id: string };
  }>({
    limit,
    skip,
    content_type: 'article',
    'fields.categories.sys.id': categoryTopicSysId,
    'fields.discoverable': true,
    select: 'sys.id',
    order: getArticlesOrder(sortMode),
    ...(excludeIds && { 'sys.id[nin]': excludeIds }),
  });

  return items.map(({ sys: { id } }) => id);
};

export const getLastPublishedArticleIdByTagName = async (
  tagName: string,
): Promise<string | null> => {
  const homepageFeaturedTagId = await getTagIdByTagName(tagName);
  if (!homepageFeaturedTagId) {
    return null;
  }

  const entries = await contentfulRESTClient.getEntries<{
    sys: { id: string };
  }>({
    limit: 1,
    skip: 0,
    content_type: 'article',
    'fields.tags.sys.id': homepageFeaturedTagId,
    select: 'sys.id',
    order: '-fields.editorialLastPublishedDateComputed',
  });

  return entries.items[0] ? entries.items[0].sys.id : null;
};

export const getTagIdByTagName = async (tagName: string) => {
  const { items } = await contentfulRESTClient.getEntries<{
    sys: { id: string };
  }>({
    limit: 1,
    skip: 0,
    content_type: 'tagTopic',
    'fields.title': tagName,
    select: 'sys.id',
  });

  return items[0]?.sys.id || null;
};

const ArticlesBodiesQuery = gql`
  query ArticlesBodies($slugs: [String!]) {
    articleCollection(where: { slug_in: $slugs }) {
      items {
        slug
        body {
          json
        }
      }
    }
  }
`;

export const fetchArticlesBodiesBySlugs = async (slugs: string[]) => {
  const ContentfulClient = getContentfulClient(false);
  const { data }: ApolloQueryResult<ArticlesBodies> =
    await ContentfulClient.query({
      query: ArticlesBodiesQuery,
      variables: {
        slugs,
      },
      errorPolicy: 'none',
    });

  return data.articleCollection?.items.length
    ? data.articleCollection.items
    : [];
};
